import React, { useContext, useEffect, useState } from "react";
import { AppContext } from "../context";
import Navbar from "./Navbar";
import {
  patLogs,
  userLogs,
  getImageCountByTag,
} from "../context/utils";
import { exitFullscreenAndUnlock } from "../utils_new/orientation";

/**
 * Survey review & declaration -- the last stop between the menu's submit button
 * and the upload screen.
 *
 * It states what the survey actually contains, takes the surveyor's declaration,
 * and only then hands over to Success (components/Success.js), which does the
 * upload. Nothing is captured or uploaded here, so it is read in whichever way
 * the phone is held: the flow's landscape lock is released on the way in, the
 * same as the document upload screen does.
 *
 * There is deliberately NO back button. The menu has already been submitted by
 * the time this renders -- for a client on the image-feedback flow that submit
 * started analysis -- so the only way on is forward.
 *
 * Every string and every summary row is configurable under config["review-page"],
 * named for the app's other per-screen keys (success-page, menu-page). The
 * defaults below are what a client sees when the key is absent:
 *
 *   title, summary-title, declaration-title, declaration-text, btn-text,
 *   completed-suffix, uploaded-suffix   -- plain strings
 *   summary                             -- the rows, replacing DEFAULT_SUMMARY
 *
 * A summary row is { id, label, icon, source, ... }. `icon` names one of ICONS
 * below. `source` says where the value comes from, and each one takes its own
 * extra keys:
 *
 *   block        one module's progress, as "done / total Completed". `match`
 *                names it, against a block's id, name or tag, and may be a list
 *                of names when a module is called different things by different
 *                clients.
 *   images       how many images a module has produced, as a plain count.
 *                `tags` counts them for real, by the tags the module captures
 *                under; otherwise `match` names the module and `images`
 *                (default 1) is what it yields, credited once it is complete.
 *   otherModules how many modules are done outside the ones already itemised;
 *                `exclude` names those, matched like `match`.
 *   damagePhotos how many damage photos went to the upload queue, counted by
 *                tag (`tag`, default "Damage").
 *   documents    required document categories in, as "done / total Uploaded",
 *                read from the same config and state the document upload screen
 *                uses (components/LibertyDocument.js).
 *   static       `value`, written straight into the config.
 *
 * A row whose source has nothing to report is left out rather than shown empty,
 * so one config suits a flow with no damage module or no documents.
 */

/** Lowercase, and reduce punctuation to single spaces, for tolerant matching. */
const normalise = (value) =>
  String(value === undefined || value === null ? "" : value)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

/**
 * Icon per summary row, picked by the row's `icon` key. Stroked line art at
 * 24x24, matching the document upload screen's icons.
 */
/**
 * Icon per summary row, picked by the row's `icon` key.
 *
 * Most are stroked line art on a 24x24 box. The camera is the solid artwork the
 * document upload screen uses, so the two screens show the same one; it is
 * filled rather than stroked and drawn on 48x48, so every entry carries its own
 * box and says how it is painted.
 */
const ICONS = {
  camera: {
    viewBox: "0 0 48 48",
    filled: true,
    paths: (
      <path d="M37.8964844,39.953125H10.1035156c-3.2421875,0-5.8798828-2.6376953-5.8798828-5.8798828V20.1293945 c0-3.2421875,2.6376953-5.8798828,5.8798828-5.8798828h2.9741211c0.5141602,0,0.9658203-0.3291016,1.1235352-0.8183594 l0.484375-1.5043945c0.7470703-2.3212891,2.8876953-3.8803711,5.3256836-3.8803711h7.9780273 c2.4375,0,4.578125,1.559082,5.3251953,3.8798828l0.484375,1.5053711c0.1572266,0.4892578,0.6083984,0.8178711,1.1230469,0.8178711 h2.9746094c3.2421875,0,5.8798828,2.6376953,5.8798828,5.8798828v13.9438477 C43.7763672,37.3154297,41.1386719,39.953125,37.8964844,39.953125z M10.1035156,17.2495117 c-1.5878906,0-2.8798828,1.2919922-2.8798828,2.8798828v13.9438477c0,1.5878906,1.2919922,2.8798828,2.8798828,2.8798828h27.7929688 c1.5878906,0,2.8798828-1.2919922,2.8798828-2.8798828V20.1293945c0-1.5878906-1.2919922-2.8798828-2.8798828-2.8798828H34.921875 c-1.8212891,0-3.4208984-1.1650391-3.9785156-2.8989258l-0.484375-1.5048828 c-0.3466797-1.0761719-1.3388672-1.7993164-2.4697266-1.7993164h-7.9780273c-1.1308594,0-2.1235352,0.7231445-2.4702148,1.7998047 l-0.484375,1.5048828c-0.5585938,1.7338867-2.1577148,2.8984375-3.9790039,2.8984375H10.1035156z M24,35.0322266 c-4.8515625,0-8.7988281-3.9472656-8.7988281-8.7988281c0-4.8520508,3.9472656-8.7993164,8.7988281-8.7993164 s8.7988281,3.9472656,8.7988281,8.7993164C32.7988281,31.0849609,28.8515625,35.0322266,24,35.0322266z M24,20.434082 c-3.1972656,0-5.7988281,2.6015625-5.7988281,5.7993164c0,3.1972656,2.6015625,5.7988281,5.7988281,5.7988281 s5.7988281-2.6015625,5.7988281-5.7988281C29.7988281,23.0356445,27.1972656,20.434082,24,20.434082z" />
    ),
  },
  gauge: {
    viewBox: "0 0 24 24",
    paths: (
      <>
        <path d="M4 17a8 8 0 1116 0" />
        <path d="M12 17l4.2-4.2" />
        <path d="M3.6 17h16.8" />
      </>
    ),
  },
  layers: {
    viewBox: "0 0 24 24",
    paths: (
      <>
        <path d="M12 3l8 4.4-8 4.4-8-4.4z" />
        <path d="M4 12.4l8 4.4 8-4.4" />
        <path d="M4 16.6l8 4.4 8-4.4" />
      </>
    ),
  },
  photo: {
    viewBox: "0 0 24 24",
    paths: (
      <>
        <rect x="3" y="5.5" width="18" height="13" rx="2.2" />
        <circle cx="8.6" cy="10.4" r="1.5" />
        <path d="M21 15.5l-4.6-4.6L9 18.5" />
      </>
    ),
  },
  doc: {
    viewBox: "0 0 24 24",
    paths: (
      <>
        <path d="M7 3h7l4 4v14H7z" />
        <path d="M14 3v4h4" />
        <path d="M9 13h6M9 17h4" />
      </>
    ),
  },
};

const RowIcon = ({ name }) => {
  const icon = ICONS[name] || ICONS.doc;
  // Solid artwork takes currentColor as its fill; line art is stroked with it.
  return icon.filled ? (
    <svg viewBox={icon.viewBox} fill="currentColor">
      {icon.paths}
    </svg>
  ) : (
    <svg
      viewBox={icon.viewBox}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {icon.paths}
    </svg>
  );
};

/**
 * The summary shown when the client config carries no `review-page.summary`.
 *
 * Each row names where its number comes from rather than carrying one, so the
 * page reports the survey that was actually done. A row whose source has nothing
 * to report -- no damage module in the flow, no documents configured -- is left
 * out rather than shown empty.
 */
const DEFAULT_SUMMARY = [
  {
    id: "images360",
    label: "360 Vehicle Images",
    icon: "camera",
    source: "block",
    match: "360 Image",
  },
  {
    /* The VIN and odometer module marks itself done as a whole rather than per
       frame, so its yield is stated here: two images, credited once it is. */
    id: "vinOdo",
    label: "Vin & Odometer Images",
    icon: "gauge",
    source: "images",
    match: ["VinOdo Video Capture", "NewVinOdo Video Capture", "VinOdo", "Odometer"],
    images: 2,
  },
  {
    /* Whatever else the flow carries, counted as completed modules -- the four
       named above are the ones already itemised on this screen. */
    id: "additional",
    label: "Additional Sections",
    icon: "layers",
    source: "otherModules",
    exclude: ["360 Image", "VinOdo", "damage", "Document"],
  },
  {
    id: "damagePhotos",
    label: "Damage Photos Uploaded",
    icon: "photo",
    source: "damagePhotos",
  },
  {
    id: "documents",
    label: "Mandatory Documents",
    icon: "doc",
    source: "documents",
  },
];

const DEFAULT_DECLARATION =
  "I hereby confirm that the information provided is true and correct to the best of my knowledge. I understand that any false information may lead to claim rejection.";

const LibertyReview = () => {
  const {
    config,
    condition,
    subCondition,
    currentDamage,
    docs,
    inspectionId,
    setScreen,
    setCurrentPage,
  } = useContext(AppContext);

  const [accepted, setAccepted] = useState(false);

  // Same primary colour as every other button in the app; the navy fallback is
  // the shade the Liberty screens were designed in.
  const accent =
    (config && config["colors"] && config["colors"]["btn"]) || "#1c1550";

  const page =
    (config && config["review-page"] && typeof config["review-page"] === "object"
      ? config["review-page"]
      : {}) || {};

  /** The client's translation for `value`, or `value` itself. Mirrors the
   *  pattern used across the app. */
  const getTranslateText = (value) => {
    if (
      config &&
      config["language"] &&
      Object.keys(config["language"]).includes(value)
    ) {
      return config["language"][value];
    }
    return value;
  };

  /** Configured string, or the built-in default put through the language map. */
  const text = (key, fallback) => {
    if (Object.keys(page).includes(key) && page[key]) return page[key];
    return getTranslateText(fallback);
  };

  // On arrival, once. This screen is read and signed rather than aimed at a car,
  // so it drops the flow's fullscreen landscape lock and lays out for either
  // orientation. The lock is not restored on the way out: the only screen after
  // this one is Success, which reads in both orientations too.
  useEffect(() => {
    exitFullscreenAndUnlock();
    setCurrentPage("Review & Declaration Page");
    patLogs("Entered to review and declaration page", inspectionId);
    userLogs({
      position: 8,
      last_page: "review and declaration page",
      inspectionId,
    });
  }, []);

  const blocks = Array.isArray(config && config["blocks"])
    ? config["blocks"]
    : [];

  /**
   * The configured block whose id, name or tag matches, else null.
   *
   * `match` is one name or a list of them, because a module is not named the
   * same in every client's config -- the VIN and odometer capture alone appears
   * as "VinOdo Video Capture" and "NewVinOdo Video Capture". Every candidate is
   * tried exactly before any is tried as a substring, so a loose name like
   * "VIN" cannot beat an exact one to the answer.
   */
  const findBlock = (match) => {
    const wanted = (Array.isArray(match) ? match : [match])
      .map(normalise)
      .filter(Boolean);
    if (wanted.length === 0) return null;
    const names = (b) =>
      [b && b.id, b && b.name, b && b.tag].filter(
        (v) => typeof v === "string" && v.length > 0
      );
    for (const w of wanted) {
      const exact = blocks.find((b) => names(b).some((v) => normalise(v) === w));
      if (exact) return exact;
    }
    for (const w of wanted) {
      const partial = blocks.find((b) =>
        names(b).some((v) => normalise(v).includes(w))
      );
      if (partial) return partial;
    }
    return null;
  };

  const hasDamageModule = blocks.some((b) =>
    [b && b.name, b && b.tag].some(
      (v) => typeof v === "string" && normalise(v) === "damage"
    )
  );

  const counted = (done, total, suffix) => ({
    text: `${done} / ${total} ${suffix}`.trim(),
    done: total > 0 && done === total,
  });

  /**
   * A row's value, or null when its source has nothing to report -- the caller
   * drops those rows rather than printing a blank.
   */
  const resolveRow = (row) => {
    const source = row && row.source ? String(row.source) : "static";

    if (source === "block") {
      const block = findBlock(row.match || row.block || row.label);
      if (!block) return null;
      const subs = subCondition && subCondition[block.id];
      if (subs && typeof subs === "object" && Object.keys(subs).length > 0) {
        const keys = Object.keys(subs);
        return counted(
          keys.filter((k) => subs[k] === "completed").length,
          keys.length,
          text("completed-suffix", "Completed")
        );
      }
      return counted(
        condition && condition[block.id] === "completed" ? 1 : 0,
        1,
        text("completed-suffix", "Completed")
      );
    }

    if (source === "images") {
      // Real tags when the client knows them, else the module's stated yield.
      if (Array.isArray(row.tags) && row.tags.length > 0) {
        const total = row.tags.reduce(
          (sum, tag) => sum + getImageCountByTag(tag),
          0
        );
        return { text: String(total), done: total > 0 };
      }
      const block = findBlock(row.match || row.block || row.label);
      if (!block) return null;
      const per = Number(row.images) > 0 ? Number(row.images) : 1;
      const subs = subCondition && subCondition[block.id];
      // A module with sub-sections yields per-section; one without yields its
      // whole count, and only once it is finished.
      if (subs && typeof subs === "object" && Object.keys(subs).length > 0) {
        const done = Object.keys(subs).filter(
          (k) => subs[k] === "completed"
        ).length;
        return { text: String(done * per), done: done > 0 };
      }
      const complete = condition && condition[block.id] === "completed";
      return { text: String(complete ? per : 0), done: complete };
    }

    if (source === "otherModules") {
      // Everything the flow carries beyond the modules already itemised in
      // their own rows, which `exclude` names.
      const excluded = Array.isArray(row.exclude) ? row.exclude : [];
      const isExcluded = (b) =>
        excluded.some((wanted) => {
          const w = normalise(wanted);
          return (
            !!w &&
            [b && b.id, b && b.name, b && b.tag].some(
              (v) => typeof v === "string" && normalise(v).includes(w)
            )
          );
        });
      const rest = blocks.filter((b) => b && b.id && !isExcluded(b));
      if (rest.length === 0) return null;
      const done = rest.filter(
        (b) => condition && condition[b.id] === "completed"
      ).length;
      return { text: String(done), done: done === rest.length };
    }

    if (source === "damagePhotos") {
      // Nothing to report when the flow carries no damage module at all.
      if (!hasDamageModule) return null;
      // What the damage module actually put on the upload queue, counted by its
      // own tag. A resumed case can carry damages captured before this session,
      // which that tally has never seen, so the module's own round counter
      // stands in when the tally is empty.
      const tag = row.tag || "Damage";
      const uploaded = getImageCountByTag(tag);
      const total = uploaded > 0 ? uploaded : Number(currentDamage) || 0;
      return { text: String(total), done: total > 0 };
    }

    if (source === "documents") {
      const cats = Array.isArray(config && config["documentCategories"])
        ? config["documentCategories"]
        : [];
      const required = cats.filter((c) => c && c.required);
      const list = required.length > 0 ? required : cats;
      if (list.length === 0) return null;
      const uploaded = list.filter(
        (c) => docs && Array.isArray(docs[c.id]) && docs[c.id].length > 0
      ).length;
      return counted(
        uploaded,
        list.length,
        text("uploaded-suffix", "Uploaded")
      );
    }

    // "static": a value written straight into the config.
    if (row.value === undefined || row.value === null || row.value === "")
      return null;
    return { text: String(row.value), done: row.done !== false };
  };

  const configuredSummary = Array.isArray(page["summary"])
    ? page["summary"]
    : DEFAULT_SUMMARY;

  const rows = configuredSummary
    .map((row) => ({ row, value: resolveRow(row) }))
    .filter((entry) => entry.value !== null);

  const handleSubmit = () => {
    if (!accepted) return;
    patLogs(
      "Declaration accepted, AI survey submitted from review page",
      inspectionId
    );
    userLogs({
      position: 8,
      declaration_accepted: "yes",
      inspectionId,
    });
    setScreen("success");
  };

  return (
    <div className="survey-page" style={{ "--survey-accent": accent }}>
      <Navbar />

      <div className="survey-card">
        {/* Title only: the menu has already been submitted, so there is nothing
            to go back to. */}
        <div className="survey-topbar">
          <h1 className="survey-title">
            {text("title", "Review & Declaration")}
          </h1>
        </div>

        <div className="survey-body">
          {rows.length > 0 && (
            <div className="survey-col survey-col-summary">
              <h2 className="survey-section-label">
                {text("summary-title", "Survey Summary")}
              </h2>
              <div className="survey-summary">
                {rows.map(({ row, value }, index) => (
                  <div className="survey-row" key={row.id || index}>
                    <span className="survey-row-icon">
                      <RowIcon name={row.icon} />
                    </span>
                    <span className="survey-row-label">
                      {getTranslateText(row.label || "")}
                    </span>
                    <span
                      className={`survey-row-value ${
                        value.done ? "survey-done" : ""
                      }`}
                    >
                      {value.text}
                    </span>
                    <span
                      className={`survey-row-status ${
                        value.done ? "survey-done" : ""
                      }`}
                    >
                      {value.done ? (
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3.4"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                      ) : null}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="survey-col survey-col-declaration">
            <div className="survey-declaration">
              <h2 className="survey-section-title">
                {text("declaration-title", "Declaration")}
              </h2>
              {/* The label wraps the box, so tapping the text toggles it; no
                  htmlFor, which would name the same control twice. */}
              <label className="survey-consent">
                <input
                  type="checkbox"
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                />
                <span className="survey-consent-text">
                  {text("declaration-text", DEFAULT_DECLARATION)}
                </span>
              </label>
            </div>
          </div>

          {/* Last in the one column the page runs in: summary, then
              declaration, then the action. Inside the scroll rather than
              pinned under it, so the three read in that order and the page
              scrolls as one. */}
          <div className="survey-footer">
            <button
              className={`survey-submit-btn ${
                accepted ? "survey-enabled" : ""
              }`}
              onClick={handleSubmit}
              disabled={!accepted}
            >
              {text("btn-text", "Submit AI Survey")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LibertyReview;
